/**
 * Bürozugänge anlegen und Passwörter zurücksetzen.
 *
 * Das geht nicht direkt gegen die Datenbank, sondern über die Edge Function
 * `buero-konto`. Grund: Ein Konto in `auth.users` anzulegen braucht den
 * Dienstschlüssel, und der darf niemals im Browser liegen — er hebelt die
 * Zeilensicherheit vollständig aus. Also läuft genau dieser eine Schritt auf
 * dem Server.
 *
 * Die Funktion prüft selbst, wer anruft, und lässt nur ein aktives Konto mit
 * Adminrechten durch. Die Oberfläche blendet die Knöpfe zusätzlich aus — das
 * ist Höflichkeit, nicht Sicherheit.
 *
 * Hier wird bewusst `fetch` benutzt und nicht `functions.invoke`: Letzteres
 * verpackt Fehler so, dass der eigentliche Text („Diese E-Mail-Adresse wird
 * bereits benutzt") erst aus einem verschachtelten Objekt gepult werden muss.
 * Genau dieser Text ist aber das Einzige, was im Büro weiterhilft.
 */

import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '../supabase';
import { Speicherfehler } from './speichern';

export type Vorgang = 'anlegen' | 'passwort_neu' | 'email_aendern';

export type Zugangsergebnis = {
  name: string;
  email: string;
  /** Nur beim Anlegen und Zurücksetzen — und nur dieses eine Mal. */
  passwort?: string;
};

export async function zugangVerwalten(
  was: Vorgang,
  mitarbeiterId: string,
  email?: string,
): Promise<Zugangsergebnis> {
  const { data } = await supabase.auth.getSession();
  const zeichen = data.session?.access_token;
  if (!zeichen) {
    throw new Speicherfehler({
      art: 'anmeldung',
      titel: 'Anmeldung abgelaufen',
      text: 'Bitte melde dich neu an und versuche es noch einmal.',
      original: 'keine Sitzung vorhanden',
    });
  }

  let antwort: Response;
  try {
    antwort = await fetch(`${SUPABASE_URL}/functions/v1/buero-konto`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${zeichen}`,
        apikey: SUPABASE_PUBLISHABLE_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ was, mitarbeiter_id: mitarbeiterId, email }),
    });
  } catch {
    throw new Speicherfehler({
      art: 'netz',
      titel: 'Keine Verbindung',
      text: 'Der Server ist nicht erreichbar. Es wurde nichts geändert.',
      original: 'fetch fehlgeschlagen',
    });
  }

  const koerper = (await antwort.json().catch(() => ({}))) as {
    fehler?: string;
  } & Zugangsergebnis;

  if (!antwort.ok) {
    throw new Speicherfehler({
      art: antwort.status === 403 ? 'kein_recht' : 'regel',
      titel: 'Das hat nicht geklappt',
      text: koerper.fehler ?? `Der Server antwortete mit ${antwort.status}.`,
      original: koerper.fehler ?? String(antwort.status),
    });
  }

  return koerper;
}
